import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, withRouter } from 'react-router-dom';
import styled, { css } from 'styled-components';
import {
  chunk, compose, every,
  getOr, identity, includes,
  isEmpty, isNull, keys,
  map, mapValues, nth,
  pickBy, reduce, size,
  some,
} from 'lodash/fp';
import { FormattedMessage } from 'react-intl';
import dayjs from 'dayjs';
import { Checkbox, TableRow, TableCell } from '@material-ui/core';
import { Shimmer, Table } from '../../common/components';
import messages from '../messages';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import DeleteReconfirmDialog from './DeleteReconfirmDialog';
import TableToolBar from './TableToolBar';

const cols = [
  { id: 'checkbox', style: css` width: 5%;` },
  { id: 'number', style: css`width: 15%;` },
  { id: 'title', style: css`width: 50%;` },
  { id: 'writer', style: css`width: 15%;` },
  { id: 'saveAt', style: css`width: 15%;` },
];

const S = {
  Checkbox: styled(({ classes, dark, ...otherProps }) => (
    <Checkbox
      classes={{
        ...classes,
        checked: 'checked',
      }}
      {...otherProps}
    />
  ))`
    color: ${({ dark, theme }) => theme.palette.grey[dark ? 500 : 400]};
    :hover {
      color: ${({ theme }) => theme.palette.grey[500]};
    }
    &.checked {
      color: ${({ theme }) => theme.palette.primary.light};
    }
  `,
  Link: styled(Link)`
    display: flex;
    align-items: center;
    height: 100%;
    :hover {
      text-decoration: underline;
    }
  `,
  TableRow: styled(({ classes, ...otherProps }) => (
    <TableRow
      classes={{
        ...classes,
        hover: 'hover',
      }}
      {...otherProps}
    />
  ))`
    height: 60px;
    &.hover:hover {
      background-color: ${({ theme }) => theme.palette.blueGrey[25]};
    }
  `,
  TableCell: styled(({ active, ...otherProps }) => <TableCell {...otherProps} />)`
    padding: 0;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    :nth-child(1) {
      text-align: center;
    }
    :nth-child(3) {
      position: relative;
      padding: 0 3rem;
      font-size: ${({ theme }) => theme.typography.pxToRem(15)};
      font-weight: 500;
    }
    :nth-child(4) {
      text-align: center;
    }
    :nth-child(5) {
      text-align: center;
    }
    ${({ variant, theme }) => (variant === 'head')
      && css`
        && {
          color: ${theme.palette.grey[600]};
          font-size: ${theme.typography.pxToRem(13)};
        }
      `}
  `,
  Mark: styled.mark`
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.palette.status.info};
    background-color: transparent;
    font-size: ${({ theme }) => theme.typography.pxToRem(12)};
    font-weight: 600;
  `,
  Wrapper: styled.div`
    width: 100%;
    position: relative;
  `,
  TableToolBar: styled(TableToolBar)`
    position: absolute;
    top: 0;
    left: 0;
  `,
};

const DraftTable = ({
  location, match, drafts, pageNumber, rowsPerPage, deleteDrafts,
}) => {
  const [checked, setChecked] = useState(null);
  const [open, setOpen] = useState(null);
  const [prevDrafts, setPrevDrafts] = useState({ data: null, loading: false, error: null });

  const checkedAll = useCallback(compose(
    every(identity),
    pickBy((_, key) => {
      const rows = compose(
        map(item => item.id),
        nth(pageNumber - 1),
        chunk(rowsPerPage),
      )(drafts.data);

      return includes(key, rows);
    }),
  ), [drafts.data, pageNumber, rowsPerPage]);

  const toggleAllChecked = useCallback(() => {
    const nids = compose(
      map(draft => draft.id),
      nth(pageNumber - 1),
      chunk(rowsPerPage),
    )(drafts.data);

    setChecked(prevChecked => (every(identity, prevChecked)
      ? mapValues(() => false, prevChecked)
      : {
        ...prevChecked,
        ...reduce((acc, cur) => ({
          ...acc,
          [cur]: !checkedAll(prevChecked),
        }), {}, nids),
      }));
  }, [checkedAll, drafts.data, pageNumber, rowsPerPage]);

  const checkAll = useCallback(() => setChecked(mapValues(() => true)), []);
  const uncheckAll = useCallback(() => setChecked(mapValues(() => false)), []);

  const openDeleteConfirmDialog = useCallback(() => setOpen('deleteConfirm'), []);
  const openDeleteReconfirmDialog = useCallback(() => setOpen('deleteReconfirm'), []);
  const closeDialog = useCallback(() => setOpen(null), []);

  const getRow = useCallback(draft => ({
    id: draft.id,
    cells: [
      {
        id: 'checkbox',
        content: (
          <S.Checkbox
            color="primary"
            checked={getOr(false, draft.id, checked)}
            onChange={() => setChecked(prevChecked => ({
              ...prevChecked,
              [draft.id]: !prevChecked[draft.id],
            }))}
          />
        ),
      },
      {
        id: 'number',
        content: draft.number ? (
          <FormattedMessage
            {...messages.draftTableBodyNumber}
            values={{
              year: draft.number.year,
              issueNumber: draft.number.issueNumber,
            }}
          />
        ) : '-',
      },
      {
        id: 'title',
        content: (
          <>
            <S.Mark>
              <FormattedMessage {...messages.draftTableBodyMark} />
            </S.Mark>
            <S.Link
              to={{
                pathname: `/schools/${match.params.sid}/school-newsletters/writing`,
                state: { from: location.pathname, did: draft.id },
              }}
            >
              {draft.title || <FormattedMessage {...messages.draftTableBodyTitleNoData} />}
            </S.Link>
          </>
        ),
      },
      { id: 'writer', content: draft.writer },
      {
        id: 'saveAt',
        content: dayjs(draft.updatedAt.toDate()).format('YYYY.MM.DD. HH:mm'),
      },
    ],
  }), [checked, location.pathname, match.params.sid]);
  const getRows = useCallback(compose(
    nth(pageNumber - 1),
    chunk(rowsPerPage),
    map(getRow),
  ), [getRow, pageNumber, rowsPerPage]);
  const head = useMemo(() => ({
    id: 'head',
    cells: [
      {
        id: 'checkbox',
        content: (
          <S.Checkbox
            color="primary"
            checked={
              !isNull(checked)
              && !isEmpty(checked)
              && checkedAll(checked)
            }
            onChange={toggleAllChecked}
            dark
          />
        ),
      },
      { id: 'number', content: <FormattedMessage {...messages.draftTableHeadNumber} /> },
      { id: 'title', content: <FormattedMessage {...messages.draftTableHeadTitle} /> },
      { id: 'writer', content: <FormattedMessage {...messages.draftTableHeadWriter} /> },
      { id: 'saveAt', content: <FormattedMessage {...messages.draftTableHeadSaveAt} /> },
    ],
  }), [checked, checkedAll, toggleAllChecked]);
  const body = useMemo(() => ({
    rows: getRows(drafts.data),
  }), [drafts.data, getRows]);

  const counts = useMemo(() => ({
    checked: compose(
      size,
      pickBy(identity),
    )(checked),
    total: size(drafts.data),
  }), [checked, drafts.data]);

  const dids = useMemo(() => compose(
    keys,
    pickBy(identity),
  )(checked), [checked]);

  if (drafts.data !== prevDrafts.data) {
    setChecked(reduce((acc, cur) => ({
      ...acc, [cur.id]: false,
    }), {}, drafts.data));

    setPrevDrafts(drafts);
  }


  return (
    <>
      <S.Wrapper>
        {drafts.loading
          ? <Shimmer />
          : (
            <>
              <Table
                cols={cols}
                head={head}
                body={body}
                tableRow={S.TableRow}
                tableCell={S.TableCell}
              />
              {some(identity, checked) && (
                <S.TableToolBar
                  checkAll={checkAll}
                  uncheckAll={uncheckAll}
                  checked={checked}
                  checkedAll={checkedAll}
                  toggleAllChecked={toggleAllChecked}
                  onOpen={openDeleteConfirmDialog}
                />
              )}
            </>
          )}
      </S.Wrapper>
      <DeleteConfirmDialog
        counts={counts}
        open={open === 'deleteConfirm'}
        onClose={closeDialog}
        onDelete={openDeleteReconfirmDialog}
      />
      <DeleteReconfirmDialog
        dids={dids}
        counts={counts}
        open={open === 'deleteReconfirm'}
        onClose={closeDialog}
        onDelete={deleteDrafts}
      />
    </>
  );
};

DraftTable.propTypes = {
  deleteDrafts: PropTypes.func.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      sid: PropTypes.string,
    }).isRequired,
  }).isRequired,
  drafts: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    data: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        number: PropTypes.shape({
          year: PropTypes.number.isRequired,
          issueNumber: PropTypes.string.isRequired,
        }),
        updatedAt: PropTypes.shape({
          seconds: PropTypes.number.isRequired,
          nanoseconds: PropTypes.number.isRequired,
          toDate: PropTypes.func.isRequired,
        }).isRequired,
      }),
    ),
  }),
  pageNumber: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.string.isRequired,
};

DraftTable.defaultProps = {
  drafts: null,
};

export default withRouter(DraftTable);
